import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Play, Download, Printer, Save, Trash2, BarChart3, ClipboardCheck, Pill } from "lucide-react";
import { api, errMsg, download } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, DateRange, initialRange, Select, Field, DataTable, Tabs } from "@/components/common";
import { OpsReports } from "@/components/OpsReports";

export default function Reports() {
  const { can, t, user } = useApp();
  const [cat, setCat] = useState("prep");
  const cats = [{ key: "prep", label: "PrEP Reports", icon: BarChart3 },
    ...(can("page:testing_rotation") ? [{ key: "testing", label: "Testing Rotation Reports", icon: ClipboardCheck }] : []),
    ...(can("page:medication_requests") ? [{ key: "medreq", label: "Medication Request Reports", icon: Pill }] : [])];
  return (
    <div>
      <PageHeader title={t(user.role === "employee" ? "My Reports" : user.role === "supervisor" ? "Reports" : "Reports & Analytics")} subtitle="Reports only include records you are authorized to access. All exports are PDF (US Letter 8.5 × 11 in)." />
      <Tabs tabs={cats} value={cat} onChange={setCat} testid="report-category" />
      {cat === "prep" ? <PrepReports /> : <OpsReports module={cat} />}
    </div>
  );
}

function PrepReports() {
  const { meta, t, can } = useApp();
  const [type, setType] = useState("caseload");
  const [range, setRange] = useState(initialRange("this_year"));
  const [f, setF] = useState({});
  const [rep, setRep] = useState(null);
  const [allCols, setAllCols] = useState([]);
  const [cols, setCols] = useState([]);
  const [groupBy, setGroupBy] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [saved, setSaved] = useState([]);
  const [name, setName] = useState("");
  const loadSaved = () => api.get("/reports/saved").then((r) => setSaved(r.data));
  useEffect(() => { loadSaved(); }, []);
  const filtersDisplay = () => {
    const staff = Object.fromEntries((meta?.staff || []).map((s) => [s.id, s.name]));
    const sups = Object.fromEntries((meta?.supervisors || []).map((s) => [s.id, s.name]));
    const locs = Object.fromEntries((meta?.locations || []).map((s) => [s.id, s.name]));
    return { Employee: staff[f.employee_id], Supervisor: sups[f.supervisor_id], Location: locs[f.location_id], Medication: f.medication, Pharmacy: f.pharmacy, Insurance: f.insurance, Status: f.status };
  };
  const body = (over = {}) => ({ type, start: range.start, end: range.end, filters: f, filters_display: filtersDisplay(), columns: cols.length ? cols : null, group_by: groupBy || null, sort_by: sortBy || null, sort_dir: sortDir, ...over });
  const run = async (over) => {
    try {
      const { data } = await api.post("/reports/run", body(over));
      setRep(data);
      if (!over?.keepCols) {
        setAllCols(data.columns);
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const changeType = (v) => { setType(v); setCols([]); setGroupBy(""); setSortBy(""); setRep(null); };
  const exp = async () => {
    try {
      await download("/reports/export", { method: "post", body: { ...body(), format: "pdf" }, filename: `${type}_${range.start}_${range.end}.pdf` });
    } catch (e) {
      toast.error("Export not permitted or failed");
    }
  };
  const save = async () => {
    if (!name) return toast.error("Enter a name for this configuration");
    await api.post("/reports/saved", { name, config: { type, range, f, cols, groupBy, sortBy, sortDir } });
    setName("");
    toast.success("Report configuration saved");
    loadSaved();
  };
  const loadCfg = (c) => { setType(c.type); setRange(c.range); setF(c.f || {}); setCols(c.cols || []); setGroupBy(c.groupBy || ""); setSortBy(c.sortBy || ""); setSortDir(c.sortDir || "asc"); setRep(null); };
  return (
    <div>
      <div className="no-print grid gap-4 lg:grid-cols-[1fr_280px]">
        <div className="panel space-y-4 p-4">
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Report type"><Select value={type} onChange={changeType} testid="report-type-select" options={(meta?.report_types || []).map((r, i) => ({ value: r.key, label: `${i + 1}. ${r.title}` }))} /></Field>
            <div className="md:col-span-2"><span className="lbl">Reporting period</span><div className="mt-1.5"><DateRange value={range} onChange={setRange} /></div></div>
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-7" data-testid="report-filters">
            <Select value={f.employee_id} onChange={(v) => setF({ ...f, employee_id: v })} placeholder="All employees" testid="report-filter-employee" options={(meta?.staff || []).map((s) => ({ value: s.id, label: s.name }))} />
            <Select value={f.supervisor_id} onChange={(v) => setF({ ...f, supervisor_id: v })} placeholder="All supervisors" testid="report-filter-supervisor" options={(meta?.supervisors || []).map((s) => ({ value: s.id, label: s.name }))} />
            <Select value={f.location_id} onChange={(v) => setF({ ...f, location_id: v })} placeholder="All locations" testid="report-filter-location" options={(meta?.locations || []).map((l) => ({ value: l.id, label: l.name }))} />
            <Select value={f.medication} onChange={(v) => setF({ ...f, medication: v })} placeholder="All medications" testid="report-filter-medication" options={(meta?.medications || []).map((m) => m.name)} />
            <input className="inp" placeholder="Pharmacy" value={f.pharmacy || ""} onChange={(e) => setF({ ...f, pharmacy: e.target.value })} data-testid="report-filter-pharmacy" />
            <input className="inp" placeholder="Insurance" value={f.insurance || ""} onChange={(e) => setF({ ...f, insurance: e.target.value })} data-testid="report-filter-insurance" />
            <Select value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="All statuses" testid="report-filter-status" options={meta?.statuses || []} />
          </div>
          {allCols.length > 0 && (
            <div className="grid gap-3 md:grid-cols-[1fr_200px_200px]">
              <div>
                <span className="lbl">Columns</span>
                <div className="mt-1.5 flex flex-wrap gap-2" data-testid="report-columns">
                  {allCols.map((c) => (
                    <label key={c.key} className="flex items-center gap-1.5 rounded border px-2 py-1 text-xs">
                      <input type="checkbox" checked={!cols.length || cols.includes(c.key)} data-testid={`report-col-${c.key}`}
                        onChange={(e) => { const base = cols.length ? cols : allCols.map((x) => x.key); setCols(e.target.checked ? [...base, c.key] : base.filter((x) => x !== c.key)); }} />{c.label}
                    </label>
                  ))}
                </div>
              </div>
              <Field label="Group by"><Select value={groupBy} onChange={setGroupBy} placeholder="No grouping" testid="report-group-by" options={allCols.map((c) => ({ value: c.key, label: c.label }))} /></Field>
              <Field label="Sort by"><div className="flex gap-1"><Select value={sortBy} onChange={setSortBy} placeholder="Default" testid="report-sort-by" options={allCols.map((c) => ({ value: c.key, label: c.label }))} />
                <Select className="w-24" value={sortDir} onChange={setSortDir} options={["asc", "desc"]} testid="report-sort-dir" /></div></Field>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={() => run()} data-testid="run-report-btn"><Play size={14} />{t("Run report")}</button>
            {rep && can("action:export") && <button className="btn btn-ghost" onClick={exp} data-testid="export-pdf-btn"><Download size={14} />Export PDF</button>}
            {rep && <button className="btn btn-ghost" onClick={() => window.print()} data-testid="print-report-btn"><Printer size={14} />{t("Print")}</button>}
          </div>
        </div>
        <div className="panel p-4">
          <span className="lbl">Saved configurations</span>
          <div className="mt-2 flex gap-1"><input className="inp" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="save-report-name" />
            <button className="btn btn-ghost" onClick={save} data-testid="save-report-btn"><Save size={14} /></button></div>
          <div className="mt-3 space-y-1" data-testid="saved-reports-list">
            {saved.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded px-2 py-1.5 text-sm hover:bg-[var(--surface)]">
                <button className="truncate text-left text-[#283F91] dark:text-blue-300" onClick={() => loadCfg(s.config)} data-testid={`load-saved-${s.id}`}>{s.name}</button>
                <button onClick={() => api.delete(`/reports/saved/${s.id}`).then(loadSaved)} data-testid={`delete-saved-${s.id}`}><Trash2 size={13} className="text-[var(--muted-ink)]" /></button>
              </div>
            ))}
            {!saved.length && <p className="text-xs text-[var(--muted-ink)]">None yet</p>}
          </div>
        </div>
      </div>
      {rep && (
        <div className="print-area mt-6" data-testid="report-output">
          <div className="mb-3 flex items-center gap-4">
            <img src="/brand/logo.png" alt="" className="hidden h-12 print:block" />
            <div>
              <h2 className="text-xl font-bold text-[#283F91] dark:text-blue-300" data-testid="report-title">{rep.title}</h2>
              <p className="text-xs text-[var(--muted-ink)]">Period {rep.meta.start} to {rep.meta.end} · Generated {rep.meta.generated_at} by {rep.meta.generated_by}
                {" · Filters: "}{Object.entries(rep.meta.filters_display || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ") || "None"}</p>
            </div>
          </div>
          <div className="mb-3 flex flex-wrap gap-2" data-testid="report-totals">
            {Object.entries(rep.totals).map(([k, v]) => <span key={k} className="rounded-md bg-[var(--surface)] px-3 py-1.5 text-xs"><b className="text-[#283F91] dark:text-blue-300">{v}</b> {k.replace(/_/g, " ")}</span>)}
          </div>
          <DataTable testid="report-table" rows={rep.rows.map((r, i) => ({ ...r, _i: i }))} rowKey="_i" pageSize={50}
            columns={rep.columns.map((c) => ({ key: c.key, label: c.label, render: (r) => (r[c.key] == null ? "—" : String(r[c.key])) }))} />
        </div>
      )}
    </div>
  );
}
